import React, { useEffect, useRef, useState } from 'react';

interface TableSkeletonProps {
  showHeader?: boolean;
  showFilterBar?: boolean;
  showFooter?: boolean;
  rowCount?: number;
  columnCount?: number;
  className?: string;
}

/**
 * Reusable table skeleton component that matches the actual table structure
 * Includes header, filter bar, table rows, and pagination footer
 * Matches the full viewport height behavior of ShadcnTable
 */
export function TableSkeleton({
  showHeader = true,
  showFilterBar = true,
  showFooter = true,
  rowCount = 20,
  columnCount = 9,
  className = '',
}: TableSkeletonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current && contentRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const topOffset = rect.top;
        const viewportHeight = window.innerHeight;
        const calculatedHeight = viewportHeight - topOffset;
        containerRef.current.style.setProperty(
          '--container-height',
          `${calculatedHeight}px`
        );

        // Check if content height exceeds the calculated container height
        const contentHeight = contentRef.current.scrollHeight;
        setNeedsScroll(contentHeight > calculatedHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);

    // Set up a MutationObserver to watch for changes in the content
    const observer = new MutationObserver(updateHeight);
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', updateHeight);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col ${className}`}
      style={needsScroll ? {} : { height: 'var(--container-height, 100%)' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading table data"
    >
      {/* Header Section Skeleton */}
      {showHeader && (
        <div className="flex items-center justify-between rounded-tl-md border-t border-b border-l border-edge bg-paper-light px-4 py-2">
          <div className="flex items-center gap-3">
            {/* Title skeleton */}
            <div className="h-6 w-32 rounded bg-paper-inset animate-pulse" />
          </div>
          {/* Action button skeleton */}
          <div className="h-7 w-32 rounded-md bg-paper-inset animate-pulse" />
        </div>
      )}

      {/* Filter Bar Skeleton */}
      {showFilterBar && (
        <div className="flex flex-row justify-between border-l border-edge bg-paper-light px-4 py-2">
          <div className="flex flex-row gap-2">
            {/* Filter button skeleton */}
            <div className="h-7 w-20 rounded-lg bg-paper-inset animate-pulse" />
            {/* Filter indicators skeleton */}
            <div className="h-7 w-24 rounded-lg bg-paper-inset animate-pulse" />
            <div className="h-7 w-24 rounded-lg bg-paper-inset animate-pulse" />
          </div>
          {/* Export/Column buttons skeleton */}
          <div className="flex gap-2">
            <div className="h-7 w-28 rounded-lg bg-paper-inset animate-pulse" />
            <div className="h-7 w-20 rounded-lg bg-paper-inset animate-pulse" />
          </div>
        </div>
      )}

      {/* Table Skeleton */}
      <div ref={contentRef} className="border-x border-t">
        <div className="relative w-full overflow-auto scrollbar-hide">
          <table className="w-full caption-bottom text-sm">
            {/* Table Header */}
            <thead>
              <tr className="border-b">
                {Array.from({ length: columnCount }).map((_, colIndex) => (
                  <th
                    key={`header-${colIndex}`}
                    className="my-2 h-10 bg-[#F5F3ED] px-2 text-center align-middle text-base font-medium text-ink-secondary"
                    style={{ minWidth: 'max-content' }}
                  >
                    <div className="h-4 w-20 rounded bg-edge animate-pulse mx-auto" />
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <tr
                  key={`row-${rowIndex}`}
                  className="border-b"
                  style={{
                    backgroundColor: rowIndex % 2 === 0 ? '#FCFBF8' : '#F5F3ED',
                    borderBottom: '1px solid #E8E4DC',
                  }}
                >
                  {Array.from({ length: columnCount }).map((_, colIndex) => (
                    <td
                      key={`cell-${rowIndex}-${colIndex}`}
                      className="h-10 px-2 align-middle font-medium text-ink-muted"
                      style={{ minWidth: 'max-content' }}
                    >
                      <div
                        className={`h-4 rounded bg-paper-inset animate-pulse ${
                          colIndex === 0
                            ? 'w-24'
                            : colIndex === 1
                            ? 'w-32'
                            : colIndex === 2
                            ? 'w-20'
                            : 'w-16'
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Flex-grow spacer to push footer to bottom (matches ShadcnTable structure) */}
      <div className="flex-grow border-l bg-paper-light"></div>
      
      {/* Pagination Footer Skeleton */}
      {showFooter && (
        <div className="mt-auto flex items-end justify-between space-x-2 border-l bg-paper-light px-3 py-4">
          <div className="h-5 w-48 rounded bg-paper-inset animate-pulse" />
          <div className="flex items-center space-x-1">
            <div className="h-9 w-16 rounded-md bg-paper-inset animate-pulse" />
            <div className="h-9 w-16 rounded-md bg-paper-inset animate-pulse" />
          </div>
        </div>
      )}
    </div>
  );
}
